/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '../../services/api.config';
import { useCache } from '../../providers/Cache.provider';

export type BillingPackageValues = {
  billingPackages: vp.PartnerBillingPackage[];
  mappedValues: { [id: string]: BillingPackageInfo };
  dropDownOptions: { [key: string]: string };
};

export type BillingPackageInfo = {
  packageId: string;
  packageName: string;
  dateAdded: string;
  createdBy: string;
  currency: string;
  country: string;
};

type HookProps = { api: vp.Api };
type HookTuple = [
  BillingPackageValues,
  (options?: { useCached?: boolean; country?: string }) => void,
  (id: string) => BillingPackageInfo | null,
];
type ApiHook = (props: HookProps) => HookTuple;

const useFetchBillingPackages: ApiHook = ({ api }) => {
  const source = axios.CancelToken.source();
  const { cache } = useCache();

  const [billingPackages, setBillingPackages] = useState<vp.PartnerBillingPackage[]>([]);
  const [getBillingPackages, setGetBillingPackages] = useState(false);
  const [shouldUseCache, setShouldUseCache] = useState(false);
  const [country, setCountry] = useState('');

  // Key the cache by country: the fetched list is country-filtered, so a shared key would let
  // a country-specific fetch (e.g. account creation) overwrite the entry a country-less
  // `useCached` read (e.g. Dashboard) relies on, returning the wrong subset.
  const [getCachedPackages, cachePackages] = cache<{
    billingPackages?: vp.PartnerBillingPackage[];
    mappedValues?: { [id: string]: BillingPackageInfo };
  }>(`billingPackages:${country || 'all'}`);

  const cachedPackages = getCachedPackages();

  useEffect(() => {
    const loadData = async () => {
      if (shouldUseCache && cachedPackages) {
        return setBillingPackages(cachedPackages.billingPackages || []);
      }

      const { fromObject } = vp.dataTransformers;
      const { BillingPackages } = await api.fetchBillingPackages({ Country: country || undefined }, source);

      if (BillingPackages) {
        const billingPackagesList = fromObject(BillingPackages).toArray();
        const mappedValues = buildMappedValues(billingPackagesList);

        cachePackages({ billingPackages: billingPackagesList, mappedValues });
        setBillingPackages(billingPackagesList);
      }

      return setGetBillingPackages(false);
    };

    if (getBillingPackages) void loadData();

    return () => {
      source.cancel();
    };
  }, [getBillingPackages]);

  return [
    {
      billingPackages,
      mappedValues: cachedPackages?.mappedValues || buildMappedValues(billingPackages),
      dropDownOptions: buildDropDownOptions(billingPackages),
    },
    (options?: { useCached?: boolean; country?: string }) => {
      setGetBillingPackages(true);
      setShouldUseCache(options?.useCached || false);
      setCountry(options?.country ?? '');
    },
    (packageId: string) => (cachedPackages?.mappedValues ? cachedPackages.mappedValues[packageId] : null),
  ];

  function buildDropDownOptions(packages: vp.PartnerBillingPackage[]) {
    return packages.reduce(
      (accumulator, current) => ({ ...accumulator, [current.PackageID]: current.PackageName }),
      {},
    );
  }

  function buildMappedValues(packages: vp.PartnerBillingPackage[]) {
    return packages.reduce(
      (accumulator, current) => ({
        ...accumulator,
        [current.PackageID]: {
          packageId: current.PackageID,
          packageName: current.PackageName,
          dateAdded: current.DateAdded,
          createdBy: current.CreatedBy,
          currency: current.Currency,
          country: current.Country,
        },
      }),
      {},
    );
  }
};

export default vopayApi.withApiHook<HookProps, HookTuple>(useFetchBillingPackages);
